"use client";

import { emptyWebullProductionConnection, type WebullConnectionState } from "@/lib/portfolio";

import { formatWorkspaceDate } from "@/lib/display-preferences";

import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { DashboardIcon, BarChartIcon, ReaderIcon, RocketIcon, BackpackIcon, EnvelopeClosedIcon, CheckCircledIcon, MixIcon, LayersIcon, PersonIcon, Link2Icon, Share1Icon, FileTextIcon, MagnifyingGlassIcon,  HamburgerMenuIcon, Cross2Icon, ArrowRightIcon, ArrowLeftIcon } from "@radix-ui/react-icons";
import { applyDisplayPreferences, readDisplayPreferences, defaultDisplayPreferences, workspaceViews, type DisplayPreferences } from "@/lib/display-preferences";
import AccountMenu from "./account-menu";
import DeveloperConsole from "./developer-console";
import "./internal-workspace.css";
import ExtensionWorkspace from "./extension-workspace";
import {extensionViews,extensionLabels,internalViews,type ExtensionView} from "@/lib/workspace-model";
import {useAccount} from "./workspace-context";
import {displayPreferenceKey} from "@/lib/display-preferences";
import SettingsWorkspace from "./settings-workspace";
import type {
  ConnectorCategory,
  ConnectorId,
  ConnectorResponse,
  ConnectorSnapshot,
  MediaAnalysis,
  MediaItemType,
  TranscriptMediaSourceId,
} from "@/lib/connectors/types";
import { buildProjectionSets, projectionTerminalCount } from "@/lib/projections";
import { buildKnowledgeRecords, type KnowledgeRecord } from "@/lib/knowledge";
import { apiFetch } from "@/lib/client-api";
import type { PortfolioConnectionState, PortfolioPosition } from "@/lib/portfolio";
import type { MediaDraft } from "@/lib/media";
import type { KalshiConnectionState } from "@/lib/kalshi";
import type { CoinbaseConnectionState } from "@/lib/coinbase";
import { runSimulationModels } from "@/lib/simulations";
import { systems } from "@/lib/systems";
import type { TimelineEvidenceEvent } from "@/lib/timeline-types";
import type { SignalGraphResponse, SignalPacket } from "@/lib/signal-graph";
import {
  buildTimelineSequences,
  timelineSubtypeCounts,
  type TimelineLens,
} from "@/lib/timeline-lenses";
import {
  buildSourceAuthorityGraph,
  sourceAuthorityNodeKindLabels,
} from "@/lib/source-authority";
import SourceAnalysisReading from "./source-analysis-reading";
import AgentControlWorkspace from "./agent-control-workspace";
import GlobalSearch from "./global-search";
import MediaWorkspace from "./media-workspace";
import ProjectedTimeline from "./projected-timeline";
import PortfolioWorkspace from "./portfolio-workspace";
import SimulationsWorkspace from "./simulations-workspace";
import SystemsWorkspace from "./systems-workspace";
import WorldModelWorkspace from "./world-model-workspace";

import SourceRelationshipPanel from "./source-relationship-panel";
import SourceProfileOverview from "./source-profile-overview";
import { useConfirmation } from "./use-confirmation";
import SourceIncentiveDossier from "./source-incentive-dossier";

type SourceGroup = {
  category: ConnectorCategory;
  label: string;
  description: string;
  ids: ConnectorId[];
};

type AnalysisState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ready"; analysis: MediaAnalysis };

type ProductView = ExtensionView | "timeline" | "sources" | "systems" | "simulations" | "agent" | "media" | "portfolio" | "world-model" | "settings";
type TimelineMode = "observed" | "verification" | "projected";

const timelineLensCopy: Record<TimelineLens, { title: string; lede: string }> = {
  unified: {
    title: "Unified timeline",
    lede: "Markets, systems, and media interleaved in one chronological evidence sequence.",
  },
  markets: {
    title: "Market timeline",
    lede: "Prediction markets, exchanges, and other connected market types sequenced together.",
  },
  systems: {
    title: "Systems timeline",
    lede: "Mechanisms, government and institutional evidence surfaces, and filing coverage in sequence.",
  },
  media: {
    title: "Media timeline",
    lede: "Episodes, video, newsrooms, and publications sequenced by publication time.",
  },
};

const timelineDomainLabels = {
  markets: "Markets",
  systems: "Systems",
  media: "Media",
} as const;

const emptyWebullPaperConnection: PortfolioConnectionState = {
  status: "not_configured",
  environment: "paper",
  accountCount: 0,
  positionCount: 0,
  observedAt: null,
  message: "Webull PaperTrade credentials are not configured.",
};

const emptyPolymarketConnection: PortfolioConnectionState = {
  status: "not_configured",
  environment: "live",
  accountCount: 0,
  positionCount: 0,
  observedAt: null,
  message: "Polymarket real account is not connected yet.",
};

const emptyKalshiDemoConnection: KalshiConnectionState = {
  status: "not_configured",
  environment: "paper",
  provider: "kalshi",
  kalshiEnvironment: "demo",
  accountCount: 0,
  positionCount: 0,
  observedAt: null,
  message: "Kalshi paper ledger is loading.",
  availableBalance: null,
  portfolioValue: null,
  openOrderCount: 0,
  openOrders: [],
  keyScopes: [],
  ordersEnabled: false,
  maximumOrderUsd: 10_000,
};

const emptyKalshiProductionConnection: KalshiConnectionState = {
  ...emptyKalshiDemoConnection,
  environment: "live",
  kalshiEnvironment: "production",
  message: "Kalshi production API credentials are not configured.",
  maximumOrderUsd: 100,
};

const emptyCoinbasePaperConnection: CoinbaseConnectionState = {
  status: "not_configured",
  environment: "paper",
  provider: "coinbase",
  coinbaseEnvironment: "paper",
  accountCount: 0,
  positionCount: 0,
  observedAt: null,
  availableBalance: null,
  portfolioValue: null,
  openOrderCount: 0,
  openOrders: [],
  canView: false,
  canTrade: false,
  canTransfer: false,
  ordersEnabled: false,
  maximumOrderUsd: 10_000,
  message: "Coinbase paper ledger is loading.",
};

const emptyCoinbaseProductionConnection: CoinbaseConnectionState = {
  ...emptyCoinbasePaperConnection,
  environment: "live",
  coinbaseEnvironment: "production",
  maximumOrderUsd: 100,
  message: "Coinbase production API credentials are not configured.",
};

type TimelineEvent = TimelineEvidenceEvent & {
  kind: string;
  sources: string[];
  timelineKind?: "explicit" | "derived" | "estimated";
};

const sourceGroups: SourceGroup[] = [
  {
    category: "market",
    label: "Markets",
    description: "Prices, order books, and prediction markets",
    ids: ["coinbase", "binance", "kalshi", "polymarket", "robinhood", "webull"],
  },
  {
    category: "media",
    label: "Media",
    description: "Podcasts, video, publications, transcripts, and claims",
    ids: [
      "moonshots",
      "all-in",
      "predictive-history-youtube",
      "predictive-history-substack",
      "this-week-in-startups",
      "this-week-in-ai",
      "naval-youtube",
      "naval-blog",
      "naval-substack",
      "tbpn-youtube",
      "openai-youtube",
      "openai-news",
      "openai-podcast",
      "anthropic-youtube",
      "anthropic-news",
      "tesla-media",
      "spacex-media",
      "apple-media",
      "microsoft-media",
      "google-media",
      "amazon-media",
      "meta-media",
      "nvidia-media",
      "spacexai-media",
      "wallstreetbets-reddit",
    ],
  },
  {
    category: "institution",
    label: "Institutions",
    description: "Allocators, issuers, public agencies, and data providers",
    ids: [
      "altimeter-capital",
      "blackrock",
      "a16z",
      "founders-fund",
      "khosla-ventures",
      "benchmark",
      "sequoia-capital",
      "coinbase-global",
      "sec",
      "cftc",
      "fred",
      "bls",
      "eia",
      "openai",
      "anthropic",
      "renaissance-technologies",
      "berkshire-hathaway",
      "norway-gpfg",
      "tesla",
      "spacex",
      "apple",
      "microsoft",
      "alphabet-google",
      "amazon",
      "meta",
      "nvidia",
      "spacexai",
    ],
  },
  {
    category: "person",
    label: "People",
    description: "Human source identities linked to accounts, channels, and institutions",
    ids: [
      "numanuk",
      "elon-musk",
      "chamath-palihapitiya",
      "jason-calacanis",
      "david-sacks",
      "david-friedberg",
      "peter-diamandis",
      "alex-wissner-gross",
      "salim-ismail",
      "david-blundin",
      "jiang-xueqin",
      "naval-ravikant",
      "john-coogan",
      "jordi-hays",
      "sam-altman",
      "greg-brockman",
      "fidji-simo",
      "mark-chen",
      "jakub-pachocki",
      "brad-lightcap",
      "dario-amodei",
      "daniela-amodei",
      "jared-kaplan",
      "rahul-patil",
      "krishna-rao",
      "jack-clark",
      "mike-krieger",
      "jim-simons",
      "warren-buffett",
      "brad-gerstner",
    ],
  },
];

const connectorNames: Record<ConnectorId, string> = {
  coinbase: "Coinbase",
  binance: "Binance",
  kalshi: "Kalshi",
  polymarket: "Polymarket",
  robinhood: "Robinhood",
  webull: "Webull",
  moonshots: "Moonshots Podcast",
  "all-in": "All-In Podcast",
  "predictive-history-youtube": "Predictive History",
  "predictive-history-substack": "Predictive History Substack",
  "this-week-in-startups": "This Week in Startups",
  "this-week-in-ai": "This Week in AI",
  "naval-youtube": "Naval Podcast",
  "naval-blog": "Naval Blog",
  "naval-substack": "Naval Substack",
  "tbpn-youtube": "TBPN",
  "openai-youtube": "OpenAI YouTube",
  "openai-news": "OpenAI News",
  "openai-podcast": "The OpenAI Podcast",
  "anthropic-youtube": "Anthropic YouTube",
  "anthropic-news": "Anthropic Newsroom",
  "tesla-media": "Tesla Official Media",
  "spacex-media": "SpaceX Official Media",
  "apple-media": "Apple Official Media",
  "microsoft-media": "Microsoft Official Media",
  "google-media": "Google Official Media",
  "amazon-media": "Amazon Official Media",
  "meta-media": "Meta Official Media",
  "nvidia-media": "NVIDIA Official Media",
  "spacexai-media": "SpaceXAI Official Media",
  "wallstreetbets-reddit": "r/wallstreetbets",
  numanuk: "Numan Khan (Numanuk)",
  "elon-musk": "Elon Musk",
  "chamath-palihapitiya": "Chamath Palihapitiya",
  "jason-calacanis": "Jason Calacanis",
  "david-sacks": "David Sacks",
  "david-friedberg": "David Friedberg",
  "peter-diamandis": "Peter Diamandis",
  "alex-wissner-gross": "Alex Wissner-Gross",
  "salim-ismail": "Salim Ismail",
  "david-blundin": "David Blundin",
  "jiang-xueqin": "Jiang Xueqin",
  "naval-ravikant": "Naval Ravikant",
  "john-coogan": "John Coogan",
  "jordi-hays": "Jordi Hays",
  "sam-altman": "Sam Altman",
  "greg-brockman": "Greg Brockman",
  "fidji-simo": "Fidji Simo",
  "mark-chen": "Mark Chen",
  "jakub-pachocki": "Jakub Pachocki",
  "brad-lightcap": "Brad Lightcap",
  "dario-amodei": "Dario Amodei",
  "daniela-amodei": "Daniela Amodei",
  "jared-kaplan": "Jared Kaplan",
  "rahul-patil": "Rahul Patil",
  "krishna-rao": "Krishna Rao",
  "jack-clark": "Jack Clark",
  "mike-krieger": "Mike Krieger",
  blackrock: "BlackRock",
  a16z: "Andreessen Horowitz (a16z)",
  "founders-fund": "Founders Fund",
  "khosla-ventures": "Khosla Ventures",
  benchmark: "Benchmark",
  "sequoia-capital": "Sequoia Capital",
  "coinbase-global": "Coinbase Global, Inc.",
  sec: "U.S. Securities and Exchange Commission",
  cftc: "Commodity Futures Trading Commission",
  fred: "Federal Reserve Economic Data",
  bls: "U.S. Bureau of Labor Statistics",
  eia: "U.S. Energy Information Administration",
  openai: "OpenAI",
  anthropic: "Anthropic",
  "renaissance-technologies": "Renaissance Technologies",
  "berkshire-hathaway": "Berkshire Hathaway",
  "norway-gpfg": "Norway Government Pension Fund Global",
  "jim-simons": "Jim Simons",
  "warren-buffett": "Warren Buffett",
  "brad-gerstner": "Brad Gerstner",
  "altimeter-capital": "Altimeter Capital",
  tesla: "Tesla",
  spacex: "SpaceX",
  apple: "Apple",
  microsoft: "Microsoft",
  "alphabet-google": "Alphabet / Google",
  amazon: "Amazon",
  meta: "Meta Platforms",
  nvidia: "NVIDIA",
  spacexai: "SpaceXAI (xAI)",
};

const mediaLabels: Record<MediaItemType, string> = {
  podcast: "Full episode",
  short: "Short",
  other: "Other video",
  article: "Latest article",
};

const transcriptMediaSourceIds = new Set<ConnectorId>([
  "moonshots",
  "all-in",
  "predictive-history-youtube",
  "this-week-in-startups",
  "this-week-in-ai",
  "naval-youtube",
  "tbpn-youtube",
  "openai-youtube",
  "anthropic-youtube",
]);

const sourceCountSummary = sourceGroups
  .map((group) => `${group.ids.length} ${group.label.toLowerCase()}`)
  .join(" · ");

function isTranscriptMediaSourceId(id: ConnectorId): id is TranscriptMediaSourceId {
  return transcriptMediaSourceIds.has(id);
}

function timeLabel(value: string | null) {
  if (!value) return "Not yet updated";
  return formatWorkspaceDate(new Date(value), {
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
  });
}

function dateLabel(value: string) {
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(value);
  return formatWorkspaceDate(new Date(dateOnly ? `${value}T12:00:00` : value), { month: "short", day: "numeric", year: "numeric" });
}

function timelineDateLabel(value: string) {
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(value);
  const date = new Date(dateOnly ? `${value}T12:00:00` : value);

  return formatWorkspaceDate(date, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: dateOnly ? undefined : "numeric",
    minute: dateOnly ? undefined : "2-digit",
  });
}

function normalizedEventTitle(value: string) {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function eventTime(value: string) {
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? 0 : parsed;
}

function durationLabel(seconds: number) {
  const hours = Math.floor(seconds / 3_600);
  const minutes = Math.floor((seconds % 3_600) / 60);
  return hours ? `${hours}h ${minutes}m` : `${Math.max(1, minutes)}m`;
}

function timecode(seconds: number) {
  const hours = Math.floor(seconds / 3_600);
  const minutes = Math.floor((seconds % 3_600) / 60);
  const remainder = seconds % 60;
  return hours
    ? `${hours}:${String(minutes).padStart(2, "0")}:${String(remainder).padStart(2, "0")}`
    : `${minutes}:${String(remainder).padStart(2, "0")}`;
}

function statusLabel(status: ConnectorSnapshot["status"]) {
  if (status === "live") return "Connected";
  if (status === "registered") return "Registered";
  if (status === "credentials_required") return "Needs credentials";
  return "Unavailable";
}

function capabilityRows(connector: ConnectorSnapshot) {
  if (connector.category === "institution") {
    return [
      ["Type", connector.institution?.type ?? "Institution"],
      ["Official sources", `${connector.institution?.officialSources.length ?? 0} registered`],
      ["Live updates", connector.capabilities.liveData ? "Connected" : "Not connected"],
    ];
  }

  if (connector.category === "media") {
    const channelCount = connector.media?.officialChannels?.length ?? 0;
    const isCommunity = connector.id === "wallstreetbets-reddit";
    return [
      [isCommunity ? "Community pages" : "Official channels", channelCount ? `${channelCount} registered` : "—"],
      [isCommunity ? "Post scan" : "Channel scan", connector.capabilities.liveData ? "Connected" : "Not connected"],
      ["Transcripts", connector.capabilities.transcripts ? "Available" : "Unavailable"],
    ];
  }

  if (connector.category === "person") {
    const incentiveStatus = connector.person?.incentives.status ?? "unmapped";
    return [
      ["Profiles", `${connector.person?.profiles.length ?? 0} registered`],
      ["Incentive map", incentiveStatus === "unmapped" ? "Not mapped" : incentiveStatus === "partial" ? "Partial · evidence-backed" : "Mapped"],
      ["Network", connector.person?.group ?? "Independent"],
    ];
  }

  return [
    ["Market data", connector.capabilities.liveData ? "Connected" : "Not connected"],
    ["Account data", connector.capabilities.accountData ? "Connected" : "Not connected"],
    ["Orders", connector.capabilities.orders ? "Connected" : "Disabled"],
  ];
}

function sourceCoverage(connector: ConnectorSnapshot) {
  const coverage: Partial<Record<ConnectorId, string>> = { coinbase: "Spot prices", binance: "Order books", kalshi: "Event contracts", polymarket: "Event contracts", robinhood: "Brokerage", webull: "Brokerage" };
  return coverage[connector.id] ?? connector.kind;
}

export default function Home() {
  const account = useAccount();
  const [view, setView] = useState<ProductView>("agent");
  const [operatorInternal,setOperatorInternal]=useState(false);
  const viewRef=useRef<ProductView>("agent");
  const unsavedRef=useRef(false);
  useEffect(()=>{viewRef.current=view;},[view]);
  const [unsavedWorkflow, setUnsavedWorkflow] = useState(false);
  useEffect(()=>{unsavedRef.current=unsavedWorkflow;},[unsavedWorkflow]);
  const { confirm: confirmNavigation, confirmationDialog: navigationDialog } = useConfirmation();
  async function requestView(next: ProductView) {
    if (next !== view && unsavedWorkflow && !await confirmNavigation("Leave this workflow without saving your changes?")) return;
    if (next === "documentation") { window.location.assign("/developers"); return; }
    setMobileNavOpen(false);
    if(next!==view)window.history.pushState(null,"",`#${next}`);
    setView(next);
  }
  const [timelineMode, setTimelineMode] = useState<TimelineMode>("verification");
  const [timelineRange, setTimelineRange] = useState("7");
  const [timelineLens, setTimelineLens] = useState<TimelineLens>("unified");
  const [preferences, setPreferences] = useState(defaultDisplayPreferences);
  const [preferencesSaved, setPreferencesSaved] = useState(true);
  const [navigationReady, setNavigationReady] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [sourceCategory, setSourceCategory] = useState<"all" | ConnectorCategory>("market");
  const [sourceQuery, setSourceQuery] = useState("");
  const [selectedSourceId, updateSelectedSourceId] = useState<string | null>(null);
  function setSelectedSourceId(id: string | null) { updateSelectedSourceId(id); setSourceDetailTab("overview"); window.scrollTo({top:0}); }

  const [connectors, setConnectors] = useState<ConnectorSnapshot[]>([]);
  const [generatedAt, setGeneratedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [portfolioPositions, setPortfolioPositions] = useState<PortfolioPosition[]>([]);
  const [webullProductionConnection, setWebullProductionConnection] = useState<WebullConnectionState>(emptyWebullProductionConnection);
  const [webullPaperConnection, setWebullPaperConnection] = useState<PortfolioConnectionState>(
    emptyWebullPaperConnection,
  );
  const [polymarketConnection, setPolymarketConnection] = useState<PortfolioConnectionState>(
    emptyPolymarketConnection,
  );
  const [kalshiDemoConnection, setKalshiDemoConnection] = useState<KalshiConnectionState>(
    emptyKalshiDemoConnection,
  );
  const [kalshiProductionConnection, setKalshiProductionConnection] = useState<KalshiConnectionState>(
    emptyKalshiProductionConnection,
  );
  const [coinbasePaperConnection, setCoinbasePaperConnection] = useState<CoinbaseConnectionState>(
    emptyCoinbasePaperConnection,
  );
  const [coinbaseProductionConnection, setCoinbaseProductionConnection] = useState<CoinbaseConnectionState>(
    emptyCoinbaseProductionConnection,
  );
  const [portfolioLoading, setPortfolioLoading] = useState(true);
  const [portfolioError, setPortfolioError] = useState<string | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [sourceDetailTab, setSourceDetailTab] = useState("overview");
  const [systemFocusId, setSystemFocusId] = useState<string | null>(null);
  const [detailFocus, setDetailFocus] = useState<{ view: string; id: string; snapshotId?: string; researchCycle?: string } | null>(null);
  const [simulationRevision, setSimulationRevision] = useState(0);
  const [analyses, setAnalyses] = useState<
    Partial<Record<TranscriptMediaSourceId, AnalysisState>>
  >({});
  const [signalGraph, setSignalGraph] = useState<SignalGraphResponse | null>(null);
  const [mediaDrafts, setMediaDrafts] = useState<MediaDraft[]>([]);

  const refreshSignalGraph = useCallback(async () => {
    try {
      const response = await apiFetch("/api/signal-graph", {
        cache: "no-store",
        headers: { accept: "application/json" },
      });
      if (!response.ok) throw new Error(`Signal Graph returned ${response.status}`);
      const payload = await response.json() as SignalGraphResponse;
      setSignalGraph(payload);
      setAnalyses((current) => {
        const next = { ...current };
        payload.packets.forEach((packet) => {
          next[packet.episode.sourceId] = { status: "ready", analysis: packet.analysis };
        });
        return next;
      });
    } catch {
      // Signal Graph status is additive; existing source and portfolio views remain available.
    }
  }, []);

  useEffect(() => {
    let active = true;
    apiFetch("/api/media", { cache: "no-store" })
      .then(async (response): Promise<{ drafts?: MediaDraft[] } | null> =>
        response.ok ? response.json() as Promise<{ drafts?: MediaDraft[] }> : null)
      .then((payload) => {
        if (active && payload?.drafts) setMediaDrafts(payload.drafts);
      })
      .catch(() => {
        // Media remains independently recoverable from its workspace.
      });
    return () => {
      active = false;
    };
  }, []);

  async function refreshConnectors() {
    setRefreshing(true);
    setError(null);

    try {
      const response = await apiFetch("/api/connectors", {
        cache: "no-store",
        headers: { accept: "application/json" },
      });
      if (!response.ok) throw new Error(`Connector service returned ${response.status}`);
      const payload = (await response.json()) as ConnectorResponse;
      setConnectors(payload.connectors);
      setGeneratedAt(payload.generatedAt);
      void refreshSignalGraph();
    } catch {
      setError("The source service could not be reached.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }

  const refreshPortfolio = useCallback(async () => {
    setPortfolioLoading(true);
    setPortfolioError(null);
    try {
      const response = await apiFetch("/api/portfolio?refresh=1", {
        cache: "no-store",
        headers: { accept: "application/json" },
      });
      const payload = await response.json() as {
        positions?: PortfolioPosition[];
        connections?: {
          webullProduction?: WebullConnectionState;
          webullPaper?: PortfolioConnectionState;
          polymarket?: PortfolioConnectionState;
          kalshiDemo?: KalshiConnectionState;
          kalshiProduction?: KalshiConnectionState;
          coinbasePaper?: CoinbaseConnectionState;
          coinbaseProduction?: CoinbaseConnectionState;
        };
        error?: string;
      };
      if (!response.ok || !payload.positions) {
        throw new Error(payload.error ?? "Portfolio storage could not be reached.");
      }
      setPortfolioPositions(payload.positions);
      setWebullProductionConnection(payload.connections?.webullProduction ?? emptyWebullProductionConnection);
      setWebullPaperConnection(payload.connections?.webullPaper ?? emptyWebullPaperConnection);
      setPolymarketConnection(payload.connections?.polymarket ?? emptyPolymarketConnection);
      setKalshiDemoConnection(payload.connections?.kalshiDemo ?? emptyKalshiDemoConnection);
      setKalshiProductionConnection(
        payload.connections?.kalshiProduction ?? emptyKalshiProductionConnection,
      );
      setCoinbasePaperConnection(
        payload.connections?.coinbasePaper ?? emptyCoinbasePaperConnection,
      );
      setCoinbaseProductionConnection(
        payload.connections?.coinbaseProduction ?? emptyCoinbaseProductionConnection,
      );
    } catch (portfolioLoadError) {
      setPortfolioError(portfolioLoadError instanceof Error
        ? portfolioLoadError.message
        : "Portfolio storage could not be reached.");
    } finally {
      setPortfolioLoading(false);
    }
  }, []);

  async function analyzeMedia(source: TranscriptMediaSourceId, force = false) {
    setAnalyses((current) => ({ ...current, [source]: { status: "loading" } }));
    try {
      const response = await apiFetch(
        `/api/media-analysis?source=${source}${force ? "&refresh=1" : ""}`,
        {
        cache: "no-store",
        headers: { accept: "application/json" },
        },
      );
      const payload = (await response.json()) as MediaAnalysis | { error?: string };
      if (!response.ok || !("sourceId" in payload)) {
        throw new Error("error" in payload ? payload.error : "Transcript analysis failed");
      }
      setAnalyses((current) => ({
        ...current,
        [source]: { status: "ready", analysis: payload },
      }));
    } catch (analysisError) {
      setAnalyses((current) => ({
        ...current,
        [source]: {
          status: "error",
          message:
            analysisError instanceof Error
              ? analysisError.message
              : "Transcript analysis failed.",
        },
      }));
    }
  }

  useEffect(() => {
    const saved = readDisplayPreferences(account.id);
    setPreferences(saved);
    applyDisplayPreferences(saved);
    const readLocation = async () => {
      const hash = window.location.hash.slice(1);
      if(hash!==viewRef.current&&(unsavedRef.current||document.querySelector('[data-unsaved="true"]'))){
        if(!await confirmNavigation("Leave this page without saving your changes?")){window.history.replaceState(null,"",`#${viewRef.current}`);return;}
      }
      if ([...workspaceViews, ...extensionViews, "settings"].includes(hash)) setView(hash as ProductView);
      else if (hash.startsWith("source-") && hash.slice(7) in connectorNames) { setView("sources"); updateSelectedSourceId(hash.slice(7)); setSourceDetailTab("overview"); window.scrollTo({top:0}); }
      else if (!hash) setView(saved.defaultScreen);
    };
    readLocation();
    setNavigationReady(true);
    window.addEventListener("hashchange", readLocation);
    return () => window.removeEventListener("hashchange", readLocation);
  }, [account.id,confirmNavigation]);

  useEffect(() => {
    void refreshPortfolio();
  }, [refreshPortfolio]);

  useEffect(() => {
    void refreshSignalGraph();
    const interval = window.setInterval(() => void refreshSignalGraph(), 5 * 60_000);
    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") void refreshSignalGraph();
    };
    document.addEventListener("visibilitychange", refreshWhenVisible);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
    };
  }, [refreshSignalGraph]);

  useEffect(() => {
    let cancelled = false;

    async function loadInitialConnectors() {
      try {
        const response = await apiFetch("/api/connectors", {
          cache: "no-store",
          headers: { accept: "application/json" },
        });
        if (!response.ok) throw new Error(`Connector service returned ${response.status}`);
        const payload = (await response.json()) as ConnectorResponse;
        if (!cancelled) {
          setConnectors(payload.connectors);
          setGeneratedAt(payload.generatedAt);
        }
      } catch {
        if (!cancelled) setError("The source service could not be reached.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void loadInitialConnectors();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    function openSearch(event: KeyboardEvent) {
      const target = event.target as HTMLElement | null;
      const isTyping = target?.matches("input, textarea, select, [contenteditable='true']");
      if (isTyping) return;
      if (event.key === "/" || ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k")) {
        event.preventDefault();
        setSearchOpen(true);
      }
    }
    window.addEventListener("keydown", openSearch);
    return () => window.removeEventListener("keydown", openSearch);
  }, []);

  const sourceById = useMemo(
    () => new Map(connectors.map((connector) => [connector.id, connector])),
    [connectors],
  );
  const sourceAuthority = useMemo(
    () => buildSourceAuthorityGraph(connectors),
    [connectors],
  );
  const sourceRelationshipCounts = useMemo(() => {
    const counts = new Map<string, number>();
    sourceAuthority.relationships.forEach((relationship) => {
      counts.set(relationship.from, (counts.get(relationship.from) ?? 0) + 1);
      counts.set(relationship.to, (counts.get(relationship.to) ?? 0) + 1);
    });
    return counts;
  }, [sourceAuthority]);
  const liveCount = connectors.filter((connector) => connector.status === "live").length;
  const registeredCount = connectors.filter(
    (connector) => connector.status === "registered",
  ).length;
  const lockedCount = connectors.filter(
    (connector) => connector.status === "credentials_required",
  ).length;

  const timelineSequences = useMemo(() => {
    const sourceNames = new Map<string, string>([
      ...(signalGraph?.sources ?? []).map((source) => [source.id, source.name] as const),
      ...connectors.map((connector) => [connector.id, connector.name] as const),
    ]);
    const sequences = buildTimelineSequences(
      connectors,
      signalGraph?.timelineObservations ?? signalGraph?.observations ?? [],
      systems,
    );
    return Object.fromEntries(
      (Object.entries(sequences) as Array<[TimelineLens, TimelineEvidenceEvent[]]>).map(
        ([lens, events]) => [lens, events.map<TimelineEvent>((event) => ({
        ...event,
        sources: event.sourceIds.map((sourceId) => sourceNames.get(sourceId) ?? sourceId),
        }))],
      ),
    ) as Record<TimelineLens, TimelineEvent[]>;
  }, [connectors, signalGraph]);
  const observedEvents = timelineSequences.unified;
  const focusedObservedEvents = timelineSequences[timelineLens];

  const signalPackets = useMemo<SignalPacket[]>(
    () => signalGraph?.packets ?? [],
    [signalGraph],
  );

  const verificationEvents = useMemo(() => {
    const events = new Map<string, TimelineEvent>();
    const persistedAnalyses = new Set(
      signalPackets.map((packet) => `${packet.episode.sourceId}:${packet.episode.videoId}`),
    );

    signalPackets.forEach((packet) => {
      packet.claims.forEach((claim) => {
        events.set(`claim:${claim.id}`, {
          id: `claim:${claim.id}`,
          at: claim.timeline.endDate,
          kind: "Claim deadline",
          sourceIds: [packet.episode.sourceId],
          sources: [packet.episode.sourceName],
          title: claim.text,
          detail: claim.timeline.rationale,
          url: claim.sourceUrl,
          timelineKind: claim.timeline.kind,
        });
      });
    });

    Object.values(analyses).forEach((state) => {
      if (!state || state.status !== "ready") return;
      if (persistedAnalyses.has(`${state.analysis.sourceId}:${state.analysis.videoId}`)) return;

      state.analysis.claims.forEach((claim) => {
        const identity = `claim:${normalizedEventTitle(claim.text)}:${claim.timeline.endDate}`;
        const existing = events.get(identity);

        if (existing) {
          if (!existing.sources.includes(state.analysis.sourceName)) {
            existing.sources.push(state.analysis.sourceName);
          }
          if (!existing.sourceIds.includes(state.analysis.sourceId)) {
            existing.sourceIds.push(state.analysis.sourceId);
          }
          return;
        }

        events.set(identity, {
          id: identity,
          at: claim.timeline.endDate,
          kind: "Claim deadline",
          sourceIds: [state.analysis.sourceId],
          sources: [state.analysis.sourceName],
          title: claim.text,
          detail: claim.timeline.rationale,
          url: claim.sourceUrl,
          timelineKind: claim.timeline.kind,
        });
      });
    });

    return Array.from(events.values()).sort(
      (left, right) => eventTime(left.at) - eventTime(right.at),
    );
  }, [analyses, signalPackets]);

  const simulationRuns = useMemo(
    () => runSimulationModels(connectors, [...observedEvents, ...verificationEvents], {
      asOf: generatedAt,
      revision: simulationRevision,
    }),
    [connectors, generatedAt, observedEvents, simulationRevision, verificationEvents],
  );
  const projectionSets = useMemo(
    () => buildProjectionSets(simulationRuns),
    [simulationRuns],
  );
  const terminalProjectionCount = projectionTerminalCount(projectionSets);

  const sequencedSourceCount = new Set(
    focusedObservedEvents.flatMap((event) => event.sourceIds),
  ).size;
  const observedBreakdown = timelineLens === "unified"
    ? (["markets", "systems", "media"] as const).map((domain) => ({
        subtype: timelineDomainLabels[domain],
        count: timelineSequences[domain].length,
      }))
    : timelineSubtypeCounts(focusedObservedEvents);
  const subtypeSummary = observedBreakdown
    .filter((entry) => entry.count > 0)
    .slice(0, 4)
    .map((entry) => `${entry.count} ${entry.subtype}`)
    .join(" · ");
  const timelineEvents =
    timelineMode === "verification" ? verificationEvents : focusedObservedEvents.filter(event => timelineRange === "all" || Date.parse(event.at) >= Date.parse(generatedAt ?? new Date().toISOString()) - Number(timelineRange) * 86400000);
  const timelineSummary = timelineMode === "projected"
    ? loading
      ? "Building trajectories"
      : `${projectionSets.length} forecast sets · ${terminalProjectionCount} terminal paths`
    : timelineMode === "verification"
      ? loading && !signalGraph
        ? "Loading claim timelines"
        : `${verificationEvents.length} claim deadlines`
      : loading
        ? "Building sequence"
        : `${focusedObservedEvents.length} events · ${sequencedSourceCount} sources`;
  const timelineSummaryDetail = timelineMode === "projected"
    ? "Forward from unified history · confidence separate"
    : timelineMode === "verification"
      ? "Explicit + estimated horizons"
      : subtypeSummary || "No sequenced evidence";
  const timelineHeading = timelineMode === "projected"
    ? "Projected timeline"
    : timelineMode === "verification"
      ? "Claim timeline"
      : timelineLensCopy[timelineLens].title;
  const timelineLede = timelineMode === "projected"
    ? "Forward projections branching from the unified observed sequence and its current evidence state."
    : timelineMode === "verification"
      ? "Extracted claims, sequenced by the date each one can be checked."
      : timelineLensCopy[timelineLens].lede;
  const knowledgeRecords = useMemo(
    () => buildKnowledgeRecords(
      connectors,
      [...observedEvents, ...verificationEvents],
      { simulationRuns, projectionSets, portfolioPositions, signalPackets, mediaDrafts },
    ),
    [connectors, mediaDrafts, observedEvents, portfolioPositions, projectionSets, signalPackets, simulationRuns, verificationEvents],
  );

  function openKnowledgeRecord(record: KnowledgeRecord) {
    setSearchOpen(false);
    void requestView(record.navigation.view);
    setDetailFocus({ view: record.navigation.view, id: record.entityId });
    if (record.navigation.timelineMode) {
      setTimelineMode(record.navigation.timelineMode);
      if (record.navigation.timelineMode === "observed") {
        setTimelineLens(record.navigation.timelineLens ?? "unified");
      }
    }
    if (record.kind === "system") setSystemFocusId(record.entityId);
    if (record.kind === "source") { setSelectedSourceId(record.entityId); setSourceCategory("all"); setSourceQuery(""); }
    if (record.kind === "signal") {
      const packet = signalPackets.find(candidate => candidate.id === record.entityId);
      if (packet) {
        setSelectedSourceId(packet.episode.sourceId);
        setSourceCategory("all");
        setSourceQuery("");
        setSourceDetailTab("claims");
      }
    }
    if (record.navigation.targetId) {
      window.setTimeout(() => {
        const target = document.getElementById(record.navigation.targetId ?? "");
        if (target instanceof HTMLDetailsElement) target.open = true;
        let parentDetails = target?.parentElement?.closest("details") ?? null;
        while (parentDetails) {
          parentDetails.open = true;
          parentDetails = parentDetails.parentElement?.closest("details") ?? null;
        }
        target?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 80);
    }
  }

  useEffect(() => {
    if (!navigationReady) return;
    window.history.replaceState(null, "", `#${view}`);
    setMobileNavOpen(false);
    window.scrollTo({ top: 0 });
  }, [navigationReady, view]);

  useEffect(() => {
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const update = () => applyDisplayPreferences(preferences);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, [preferences]);

  function savePreferences(next: DisplayPreferences) {
    setPreferences(next);
    applyDisplayPreferences(next);
    try {
      window.localStorage.setItem(displayPreferenceKey(account.id), JSON.stringify(next));
      window.localStorage.setItem("asymmetric-theme", next.appearance);
      setPreferencesSaved(true);
    } catch { setPreferencesSaved(false); }
  }

  const internalShell=internalViews.includes(view)||(view==="locallens"&&operatorInternal);
  const internalNavIds: ExtensionView[] = view==="locallens"&&operatorInternal
    ? ["company","locallens","business","investors","research","legal","team-access","environments"]
    : ["business","investors","research","legal"].includes(view)
    ? ["company","business","investors","research","legal","environments","team-access","internal-connections","audit-log"]
    : ["sales","operations","outreach","playbooks"].includes(view)
      ? ["company","sales","outreach","playbooks","operations","business","team-access","environments"]
      : ["company","environments","team-access","internal-connections","model-routing","audit-log"];
  const otherInternalIds: ExtensionView[] = (["business","sales","outreach","playbooks","operations","locallens","investors","research","legal","model-routing"] as ExtensionView[]).filter(id=>!internalNavIds.includes(id));
  if (view === "developers") return <DeveloperConsole navigate={next=>void requestView(next as ProductView)}/>;

  return (
    <main id="top" className="minimal-app approved-shell" data-internal={internalShell} data-view={view} data-nav-open={mobileNavOpen} data-navigation-ready={navigationReady}>
      {navigationDialog}
      <a className="skip-link" href="#workspace">Skip to workspace</a>

      <div className="mobile-header"><button className="brand" type="button" onClick={() => void requestView("agent")}>Asymmetric</button><button type="button" aria-label={mobileNavOpen ? "Close navigation" : "Open navigation"} aria-expanded={mobileNavOpen} onClick={() => setMobileNavOpen(value => !value)}>{mobileNavOpen ? <Cross2Icon /> : <HamburgerMenuIcon />}</button></div>
      {mobileNavOpen && <button className="mobile-nav-backdrop" type="button" aria-label="Close navigation" onClick={() => setMobileNavOpen(false)} />}
      <header className="site-header">
        <button className="brand" type="button" onClick={() => void requestView("agent")}>Asymmetric</button>
        {internalShell&&<p className="sidebar-context">Internal</p>}
        <nav className="view-switch" aria-label="Product views">
          {(internalShell ? internalNavIds.map(id=>[id,extensionLabels[id]] as const) : ([["agent", "Core"], ["locallens", "LocalLens"], ["sources", "Sources"], ["systems", "Systems"], ["timeline", "Timeline"], ["simulations", "Simulation"], ["media", "Media"], ["portfolio", "Portfolio"], ["world-model", "World Model"]] as const)).map(([id, label]) => <button key={id} type="button" aria-pressed={view === id} onClick={() => { void requestView(id); setDetailFocus(null); if (id === "sources") setSelectedSourceId(null); }}>{internalShell&&(() => {const Icon=({company:DashboardIcon,business:BarChartIcon,investors:MixIcon,research:ReaderIcon,legal:CheckCircledIcon,sales:RocketIcon,outreach:EnvelopeClosedIcon,playbooks:LayersIcon,operations:BackpackIcon,locallens:MixIcon,environments:LayersIcon,"team-access":PersonIcon,"internal-connections":Link2Icon,"model-routing":Share1Icon,"audit-log":FileTextIcon} as Record<string,typeof DashboardIcon>)[id];return Icon?<Icon aria-hidden="true"/>:<DashboardIcon aria-hidden="true"/>;})()}{label}</button>)}
        </nav>
        {internalShell&&<details className="internal-business-navigation" ><summary>More workspaces</summary><nav aria-label="Business and operations">{otherInternalIds.map(id=><button key={id} aria-pressed={view===id} onClick={()=>void requestView(id)}>{extensionLabels[id]}</button>)}</nav></details>}
        <div className="sidebar-bottom">
          <button className="sidebar-search" type="button" onClick={() => { setSearchOpen(true); setMobileNavOpen(false); }}><MagnifyingGlassIcon aria-hidden="true" />Search</button>
          {internalShell && <button type="button" onClick={() => void requestView("agent")}>Open product →</button>}
          <AccountMenu navigate={next => void requestView(next as ProductView)} />

        </div>
      </header>

      <GlobalSearch
        open={searchOpen}
        records={knowledgeRecords}
        onClose={() => setSearchOpen(false)}
        onNavigate={openKnowledgeRecord}
      />
      <AgentControlWorkspace signalGraph={signalGraph} active={view === "agent"} onOpenReference={reference => {
        const record = knowledgeRecords.find(entry => entry.kind === reference.kind && entry.entityId === reference.id);
        if (record) openKnowledgeRecord(record);
        else if (reference.kind === "source" && reference.id in connectorNames) {
          setSelectedSourceId(reference.id); setSourceCategory("all"); setSourceQuery(""); void requestView("sources");
        } else if (reference.kind === "world-model") { setDetailFocus({ view: "world-model", id: reference.id, snapshotId: "snapshotId" in reference && typeof reference.snapshotId === "string" ? reference.snapshotId : undefined }); void requestView("world-model"); }
        else if (reference.kind === "system") { setSystemFocusId(reference.id); void requestView("systems"); }
        else if (reference.kind === "simulation" || reference.kind === "portfolio" || reference.kind === "media") {
          const target = reference.kind === "simulation" ? "simulations" : reference.kind;
          setDetailFocus({ view: target, id: reference.id, researchCycle: reference.kind === "simulation" ? reference.snapshotId : undefined }); void requestView(target);
        }
      }}/>


      <MediaWorkspace active={view==="media"} focusId={detailFocus?.view==="media"?detailFocus.id:null} onDraftsChange={setMediaDrafts} onOpenSource={sourceId=>{setSelectedSourceId(sourceId);setSourceCategory("all");void requestView("sources");}}/>

      {extensionViews.includes(view as ExtensionView) ? <ExtensionWorkspace key={view} view={view as ExtensionView} onInternalModeChange={setOperatorInternal} navigate={next=>void requestView(next as ProductView)}/> : view === "settings" ? <SettingsWorkspace preferences={preferences} saved={preferencesSaved} onChange={savePreferences}
        connections={[
          { name: "Coinbase", status: coinbaseProductionConnection.status.replaceAll("_", " "), detail: coinbaseProductionConnection.message },
          { name: "Kalshi", status: kalshiProductionConnection.status.replaceAll("_", " "), detail: kalshiProductionConnection.message },
          { name: "Polymarket", status: polymarketConnection.status.replaceAll("_", " "), detail: polymarketConnection.message },
          { name: "Webull", status: webullProductionConnection.status.replaceAll("_", " "), detail: webullProductionConnection.message },
          { name: "Webull Paper", status: webullPaperConnection.status.replaceAll("_", " "), detail: webullPaperConnection.message },
        ]}
        onOpenSource={id => { setSelectedSourceId(id); setSourceCategory("all"); setSourceQuery(""); void requestView("sources"); }}
      /> : view === "timeline" ? (
        <section className="workspace timeline" id="workspace" aria-labelledby="timeline-title">
          <header className="page-heading">
            <div>
              <p className="eyebrow">Sequence</p>
              <h1 id="timeline-title">Timeline</h1>
              <p className="lede">{timelineLede}</p>
            </div>
            <div className="connection-summary" aria-live="polite">
              <strong>{timelineSummary}</strong>
              <span>{timelineSummaryDetail}</span>
            </div>
          </header>

          <div className="timeline-controls">
            <div className="mode-switch" aria-label="Timeline sequence">
              <button
                type="button"
                aria-pressed={timelineMode === "observed"}
                onClick={() => {
                  setTimelineMode("observed");
                  setTimelineLens("unified");
                }}
              >
                Observed
              </button>
              <button
                type="button"
                aria-pressed={timelineMode === "verification"}
                onClick={() => setTimelineMode("verification")}
              >
                Claims
              </button>
              <button
                type="button"
                aria-pressed={timelineMode === "projected"}
                onClick={() => setTimelineMode("projected")}
              >
                Projected
              </button>
            </div>
            <div>
              <span>Updated {timeLabel(generatedAt)}</span>
              <button
                type="button"
                onClick={() => void refreshConnectors()}
                disabled={loading || refreshing}
              >
                {refreshing ? "Refreshing" : "Refresh"}
              </button>
            </div>
          </div>

          {timelineMode === "observed" && (
            <div className="timeline-lens-controls">
              <div className="timeline-lens-switch" aria-label="Observed timeline category">
                {(["unified", "markets", "systems", "media"] as const).map((lens) => (
                  <button
                    type="button"
                    aria-pressed={timelineLens === lens}
                    onClick={() => setTimelineLens(lens)}
                    key={lens}
                  >
                    {lens === "unified" ? "Unified" : timelineDomainLabels[lens]}
                  </button>
                ))}
              </div>
              <label className="timeline-range"><span className="sr-only">Time range</span><select value={timelineRange} onChange={event => setTimelineRange(event.target.value)}><option value="7">Last 7 days</option><option value="30">Last 30 days</option><option value="all">All dates</option></select></label>
              <div className="timeline-subtype-strip" aria-label={`${timelineHeading} composition`}>
                {observedBreakdown.filter((entry) => entry.count > 0).map((entry) => (
                  <span key={entry.subtype}>
                    {entry.subtype} <strong>{entry.count}</strong>
                  </span>
                ))}
              </div>
            </div>
          )}

          {error && (
            <div className="service-error" role="alert">
              <span>{error}</span>
              <button type="button" onClick={() => void refreshConnectors()}>Try again</button>
            </div>
          )}

          {timelineMode === "projected" ? (
            <ProjectedTimeline
              connectors={connectors}
              projectionSets={projectionSets}
              anchorTime={generatedAt}
              loading={loading}
              onOpenSystems={() => void requestView("systems")}
              onOpenSimulations={() => void requestView("simulations")}
            />
          ) : (
            <div className="timeline-list" aria-busy={loading} aria-live="polite">
              {loading && <p className="timeline-empty">Building sequence…</p>}

              {!loading && timelineEvents.map((event) => (
                <article
                  className="timeline-event"
                  id={`timeline-${encodeURIComponent(event.id)}`}
                  key={event.id}
                >
                  <time dateTime={event.at}>{timelineDateLabel(event.at)}</time>
                  <div className="timeline-marker" aria-hidden="true" />
                  <div className="timeline-event-content">
                    <header>
                      {event.domain && <span>{timelineDomainLabels[event.domain]}</span>}
                      <span>{event.kind}</span>
                      {event.evidenceState === "registered" && <span>Registered surface</span>}
                      {event.timelineKind && (
                        <span>{event.timelineKind === "explicit" ? "Explicit" : "Estimated"}</span>
                      )}
                    </header>
                    {event.url ? (
                      <a href={event.url} target="_blank" rel="noreferrer">{event.title}</a>
                    ) : (
                      <h2>{event.title}</h2>
                    )}
                    <details className="timeline-event-more"><summary>Details</summary><p>{event.detail}</p>
                    {event.signals && (
                      <ul className="timeline-signals">
                        {event.signals.map((signal) => <li key={signal}>{signal}</li>)}
                      </ul>
                    )}
                    <p className="muted">{event.sources.join(" · ")}</p></details><small>{event.sources.slice(0, 2).join(" · ")}{event.sources.length > 2 ? ` + ${event.sources.length - 2} more` : ""}</small>
                  </div>
                </article>
              ))}

              {!loading && timelineMode === "verification" && !verificationEvents.length && (
                <div className="timeline-empty">
                  <strong>No claim deadlines returned.</strong>
                  <span>Analyze a YouTube transcript in Sources to sequence its claim deadlines here.</span>
                  <button type="button" onClick={() => void requestView("sources")}>Open Sources</button>
                </div>
              )}

              {!loading && timelineMode === "observed" && !timelineEvents.length && !error && (
                <p className="timeline-empty">No events are available for this observed category.</p>
              )}
            </div>
          )}

          {timelineMode !== "projected" && (
            <p className="boundary">
              {timelineMode === "observed"
                ? timelineLens === "systems"
                  ? "System rows sequence the mechanism register and official evidence coverage. A registered filings, government, or data surface is not a dated filing or release; those enter only when a live update feed is connected. Orders disabled."
                  : timelineLens === "markets"
                    ? "Market rows are immutable public snapshots, grouped by market and hour. They are not account positions, execution quotes, or trade recommendations. Orders disabled."
                    : timelineLens === "media"
                      ? "Media rows are source publication records with duplicates merged across registered channels. Publication is not corroboration. Orders disabled."
                      : "The unified history interleaves observed market and media records with clearly labeled system-coverage registers. Registered surfaces are not dated releases or corroboration. Orders disabled."
                : "Verification deadlines are extracted claim horizons, not verified outcomes. Explicit and estimated dates remain distinguishable. Orders disabled."}
            </p>
          )}
        </section>
      ) : view === "sources" ? (
      <section className={`workspace sources ${selectedSourceId ? "source-detail-view" : ""}`} data-source-tab={sourceDetailTab} id="workspace" aria-labelledby="sources-title">
        <header className="page-heading">
          <div>
            {selectedSourceId && <button type="button" className="back-link" onClick={() => setSelectedSourceId(null)}><ArrowLeftIcon /> Sources</button>}
            <h1 id="sources-title">{selectedSourceId ? sourceById.get(selectedSourceId as ConnectorId)?.name ?? connectorNames[selectedSourceId as ConnectorId] ?? "Source" : "Sources"}</h1>
              <p className="lede">{selectedSourceId ? sourceById.get(selectedSourceId as ConnectorId)?.kind : "Markets, media, institutions, and people."}</p>
          </div>
          <div className="connection-summary" aria-live="polite">
            <strong>
              {loading
                ? "Checking connections"
                : `${liveCount} connected · ${registeredCount} registered · ${lockedCount} need setup`}
            </strong>
            <span>
              {sourceCountSummary}
              {sourceAuthority.coverage.humanNodeCount
                ? ` · ${sourceAuthority.coverage.linkedHumanNodeCount}/${sourceAuthority.coverage.humanNodeCount} human nodes linked`
                : ""}
            </span>
          </div>
        </header>

        <div className="source-controls">
          <span>Updated {timeLabel(generatedAt)}</span>
          <button
            type="button"
            onClick={() => void refreshConnectors()}
            disabled={loading || refreshing}
          >
            {refreshing ? "Refreshing" : "Refresh"}
          </button>
        </div>

        {error && (
          <div className="service-error" role="alert">
            <span>{error}</span>
            <button type="button" onClick={() => void refreshConnectors()}>Try again</button>
          </div>
        )}

        {selectedSourceId && <nav className="quiet-tabs source-profile-tabs" aria-label="Source sections">{["overview", ...(sourceById.get(selectedSourceId as ConnectorId)?.person ? ["interests"] : sourceById.get(selectedSourceId as ConnectorId)?.media ? ["transcript", "claims"] : sourceById.get(selectedSourceId as ConnectorId)?.institution ? ["media"] : ["markets"]), "relationships", "evidence"].map(tab => <button type="button" key={tab} aria-pressed={sourceDetailTab === tab} onClick={() => setSourceDetailTab(tab)}>{tab[0].toUpperCase()+tab.slice(1)}</button>)}</nav>}
        {!selectedSourceId && <><label className="source-search"><MagnifyingGlassIcon aria-hidden="true"/><input type="search" aria-label="Find a source" placeholder="Find a source" value={sourceQuery} onChange={event => setSourceQuery(event.target.value)} /></label><nav className="quiet-tabs source-category-tabs" aria-label="Source categories">{([["all", "All"], ["market", "Markets"], ["media", "Media"], ["institution", "Institutions"], ["person", "People"]] as const).map(([id, label]) => <button key={id} type="button" aria-pressed={sourceCategory === id} onClick={() => setSourceCategory(id)}>{label}</button>)}</nav></>}
        {!selectedSourceId && !loading && !sourceGroups.some(group => (sourceCategory === "all" || group.category === sourceCategory) && group.ids.some(id => `${connectorNames[id]} ${sourceById.get(id)?.kind ?? ""}`.toLowerCase().includes(sourceQuery.toLowerCase()))) && <p className="empty-state">No sources match “{sourceQuery}”.</p>}
        {sourceGroups.filter(group => selectedSourceId ? group.ids.includes(selectedSourceId as ConnectorId) : sourceCategory === "all" || group.category === sourceCategory).map((group) => (
          <section className="source-group" key={group.category} aria-labelledby={`${group.category}-title`}>
            <header className="group-heading">
              <h2 id={`${group.category}-title`}>{group.label}</h2>
              <span>{group.description}</span>
              <span>{group.ids.length}</span>
            </header>

            <div className="source-list" aria-busy={loading}>
              <div className="list-header" aria-hidden="true">
                <span>Source</span>
                <span>Coverage</span>
                <span>Snapshot</span>
                <span>Latency</span>
                <span>Status</span>
                <span />
              </div>

              {group.ids.filter(id => selectedSourceId ? id === selectedSourceId : `${connectorNames[id]} ${sourceById.get(id)?.kind ?? ""}`.toLowerCase().includes(sourceQuery.toLowerCase())).map((id) => {
                const connector = sourceById.get(id);
                if (!connector) {
                  return (
                    <article className="source-row loading-row" key={id}>
                      <div className="source-name">
                        <strong>{connectorNames[id]}</strong>
                        <span>Checking</span>
                      </div>
                      <span className="loading-label">Connecting…</span>
                    </article>
                  );
                }

                const transcriptSourceId = isTranscriptMediaSourceId(connector.id)
                  ? connector.id
                  : null;
                const citedPacket = detailFocus?.view === "sources"
                  ? signalPackets.find(packet => packet.id === detailFocus.id && packet.episode.sourceId === connector.id)
                  : undefined;
                const analysis: AnalysisState | undefined = citedPacket
                  ? { status: "ready", analysis: citedPacket.analysis }
                  : transcriptSourceId
                  ? analyses[transcriptSourceId]
                  : undefined;

                return (
                  <article className="source-row" id={`source-${connector.id}`} key={connector.id}>
                    <div className="source-name">
                      <button type="button" className="source-open-name" onClick={() => setSelectedSourceId(connector.id)}>{connector.name}</button>
                      <span>{connector.kind}</span>
                    </div>
                    <div className="source-signal">
                      <strong>{sourceCoverage(connector)}</strong>
                      <span>{connector.headline}</span>
                    </div>
                    <div className="source-quote">
                      <strong>{connector.primary.value}</strong>
                      <span>{connector.primary.label}</span>
                      <small>{connector.secondary.label} {connector.secondary.value}</small>
                    </div>
                    <span className="latency">
                      {connector.latencyMs === null ? "—" : `${connector.latencyMs} ms`}
                    </span>
                    <span className={`source-status ${connector.status}`}>
                      {connector.status === "live" ? "Feed live" : statusLabel(connector.status)}
                    </span>
                    <details className="source-details" open={selectedSourceId === connector.id}>
                      <summary aria-label={`View ${connector.name} source details`} onClick={event => { if (!selectedSourceId) { event.preventDefault(); setSelectedSourceId(connector.id); } }}><ArrowRightIcon aria-hidden="true" /></summary>
                      <div className="details-content">
                        {selectedSourceId && ["overview", "markets"].includes(sourceDetailTab) ? <SourceProfileOverview connector={connector} graph={sourceAuthority} onOpenSource={setSelectedSourceId} onRelationships={() => setSourceDetailTab("relationships")}/> : selectedSourceId && transcriptSourceId && ["transcript", "claims", "evidence"].includes(sourceDetailTab) ? <SourceAnalysisReading key={connector.id} analysis={analysis?.status === "ready" ? analysis.analysis : undefined} mode={sourceDetailTab} pending={analysis?.status === "loading"} error={analysis?.status === "error" ? analysis.message : undefined} supported={connector.capabilities.transcripts} onAnalyze={() => void analyzeMedia(transcriptSourceId, analysis?.status === "ready")}/> : <>

                        <p>{connector.detail}</p>
                        <dl>
                          {capabilityRows(connector).map(([label, value]) => (
                            <div key={label}><dt>{label}</dt><dd>{value}</dd></div>
                          ))}
                        </dl>
                        {!connector.person && !connector.institution && (
                          <a href={connector.docsUrl} target="_blank" rel="noreferrer">
                            {connector.linkLabel}
                          </a>
                        )}

                        <SourceRelationshipPanel
                          graph={sourceAuthority}
                          sourceId={connector.id}
                          onOpenSource={setSelectedSourceId}
                        />

                        {connector.person && (
                          <>
                            <div className="profile-links" aria-label={`${connector.name} profiles`}>
                              {connector.person.profiles.map((profile) => (
                                <a
                                  href={profile.url}
                                  key={`${profile.platform}-${profile.url}`}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  {profile.platform} · {profile.label}
                                </a>
                              ))}
                            </div>

                            {connector.incentiveModel ? (
                              <SourceIncentiveDossier model={connector.incentiveModel} />
                            ) : connector.person.incentives.status !== "unmapped" && (
                              <section className="incentive-map" aria-label={`${connector.name} incentive and proximity map`}>
                                <header>
                                  <strong>Incentives + proximity</strong>
                                  <span>{connector.person.incentives.status} · reviewed {dateLabel(connector.person.incentives.asOf)}</span>
                                </header>
                                <div className="incentive-grid">
                                  <div>
                                    <span>Role</span>
                                    <strong>{connector.person.incentives.roles[0]?.title ?? "Unknown"}</strong>
                                  </div>
                                  <div>
                                    <span>Financial exposure</span>
                                    <strong>{connector.person.incentives.financialExposures.length} evidence-bounded relationships</strong>
                                  </div>
                                  <div>
                                    <span>Information proximity</span>
                                    <strong>{connector.person.incentives.informationProximity.length} relationships · not proof of specific knowledge</strong>
                                  </div>
                                </div>
                                <div className="incentive-records">
                                  {connector.person.incentives.financialExposures.map((exposure) => (
                                    <p key={exposure.id}>
                                      <span>{exposure.targetName} · {exposure.scope.replaceAll("_", " ")}</span>
                                      {exposure.statement}
                                    </p>
                                  ))}
                                  {connector.person.incentives.informationProximity.map((proximity) => (
                                    <p key={proximity.id}>
                                      <span>{proximity.targetName} · proximity</span>
                                      {proximity.statement} {proximity.limitation}
                                    </p>
                                  ))}
                                </div>
                                <div className="institution-links" aria-label={`${connector.name} incentive evidence`}>
                                  {connector.person.incentives.evidence.map((evidence) => (
                                    <a href={evidence.url} key={evidence.id} target="_blank" rel="noreferrer">
                                      Evidence · {evidence.label}
                                    </a>
                                  ))}
                                </div>
                                <small>{connector.person.incentives.unknowns[0]}</small>
                              </section>
                            )}
                          </>
                        )}

                        {connector.institution && (
                          <div className="institution-links" aria-label={`${connector.name} official sources`}>
                            {connector.institution.officialSources.map((source) => (
                              <a
                                href={source.url}
                                key={`${source.type}-${source.url}`}
                                target="_blank"
                                rel="noreferrer"
                              >
                                {source.type} · {source.label}
                              </a>
                            ))}
                          </div>
                        )}

                        {connector.media?.officialChannels?.length ? (
                          <div className="institution-links" aria-label={`${connector.name} media channels`}>
                            {connector.media.officialChannels.map((channel) => (
                              <a
                                href={channel.url}
                                key={`${channel.type}-${channel.url}`}
                                target="_blank"
                                rel="noreferrer"
                              >
                                {channel.type} · {channel.label}
                              </a>
                            ))}
                          </div>
                        ) : null}

                        {connector.media && connector.media.items.length > 0 && (
                          <div className="media-breakdown">
                            {(connector.media.items.some((item) => item.type === "article")
                              ? (["article"] as const)
                              : (["podcast", "short", "other"] as const)
                            ).map((type) => {
                              const item = connector.media?.items.find((entry) => entry.type === type);
                              return (
                                <div className="media-item" key={type}>
                                  <span>{mediaLabels[type]}</span>
                                  {item ? (
                                    <>
                                      <a href={item.url} target="_blank" rel="noreferrer">{item.title}</a>
                                      <small>
                                        {item.durationSeconds !== undefined
                                          ? `${durationLabel(item.durationSeconds)} · `
                                          : ""}
                                        {dateLabel(item.publishedAt)}
                                      </small>
                                    </>
                                  ) : (
                                    <strong>No recent item</strong>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {connector.media && transcriptSourceId && (
                          <div className="transcript-analysis">
                            <header>
                              <div>
                                <strong>Transcript intelligence</strong>
                                <span>Full episode · six specialist agents · read only</span>
                              </div>
                              <button
                                type="button"
                                disabled={analysis?.status === "loading" || !connector.capabilities.transcripts}
                                onClick={() => void analyzeMedia(
                                  transcriptSourceId,
                                  analysis?.status === "ready",
                                )}
                              >
                                {analysis?.status === "loading"
                                  ? "Analyzing"
                                  : analysis?.status === "ready"
                                    ? "Run again"
                                    : "Analyze transcript"}
                              </button>
                            </header>

                            {!analysis && (
                              <p>
                                {connector.capabilities.transcripts
                                  ? "Extract direct claims, resolve time constraints, map source incentives and proximity, then review every result adversarially."
                                  : "The latest full episode does not currently expose a caption track, so transcript analysis is unavailable."}
                              </p>
                            )}
                            {analysis?.status === "error" && (
                              <p className="analysis-error" role="alert">{analysis.message}</p>
                            )}
                            {analysis?.status === "ready" && (
                              <div className="analysis-result">
                                <div className="analysis-meta">
                                  <span>
                                    {analysis.analysis.transcript.wordCount.toLocaleString()} transcript words processed
                                    {analysis.analysis.transcript.source === "youtube_analysis_snapshot" ? " · last completed run" : ""}
                                  </span>
                                  <span>{analysis.analysis.agentRun.explicitClaimCount} direct · {analysis.analysis.agentRun.derivedClaimCount} inferred</span>
                                  <a href={analysis.analysis.videoUrl} target="_blank" rel="noreferrer">Open full episode</a>
                                </div>
                                <div className="agent-stage-strip" aria-label="Analysis agents">
                                  {analysis.analysis.agentRun.stages.map((agentStage) => (
                                    <span key={agentStage.id}>
                                      {agentStage.name}
                                      <small>{(agentStage.durationMs / 1_000).toFixed(1)}s</small>
                                    </span>
                                  ))}
                                </div>
                                {analysis.analysis.claims.length ? (
                                  <ol className="claim-list">
                                    {analysis.analysis.claims.map((claim) => (
                                      <li key={claim.id}>
                                        <a className="claim-time" href={claim.sourceUrl} target="_blank" rel="noreferrer">
                                          {timecode(claim.atSeconds)}
                                        </a>
                                        <div className="claim-main">
                                          <span>{claim.kind === "explicit" ? "Direct claim" : "Engine inference"} · {claim.speaker.name} · {claim.modality}</span>
                                          <p>{claim.text}</p>
                                        </div>
                                        <div className="claim-timeline">
                                          <span>{claim.timeline.kind === "explicit" ? "Explicit timeline" : claim.timeline.kind === "derived" ? "Derived timeline" : "Estimated timeline"}</span>
                                          <strong>{claim.timeline.label}</strong>
                                          {claim.timeline.startDate && (
                                            <small>
                                              {dateLabel(claim.timeline.startDate)}
                                              {claim.timeline.endDate !== claim.timeline.startDate
                                                ? ` → ${dateLabel(claim.timeline.endDate)}`
                                                : ""}
                                            </small>
                                          )}
                                        </div>
                                        <details className="claim-evidence">
                                          <summary>Evidence + review</summary>
                                          {claim.evidence.map((evidence) => (
                                            <blockquote key={evidence.id}>“{evidence.quote}”</blockquote>
                                          ))}
                                          {claim.kind === "derived" && <p>{claim.derivation}</p>}
                                          <p>{claim.timeline.rationale}</p>
                                          <p>
                                            Source weight: {claim.sourceAssessment.weight}. {claim.sourceAssessment.rationale}
                                          </p>
                                          {claim.review.concerns.length > 0 && (
                                            <p>Review: {claim.review.concerns.join(" ")}</p>
                                          )}
                                          {claim.review.alternatives.length > 0 && (
                                            <p>Alternative: {claim.review.alternatives.join(" ")}</p>
                                          )}
                                          {claim.marketQuestions.length > 0 && (
                                            <p>Check next: {claim.marketQuestions.join(" · ")}</p>
                                          )}
                                        </details>
                                      </li>
                                    ))}
                                  </ol>
                                ) : (
                                  <p>No claims survived the adversarial evidence review.</p>
                                )}
                                <p className="methodology">{analysis.analysis.methodology}</p>
                              </div>
                            )}
                          </div>
                        )}
                        </>}
                      </div>
                    </details>
                  </article>
                );
              })}
            </div>
          </section>
        ))}

        <p className="boundary">
          Source ingestion only. Claims are candidates, not verified facts. Orders disabled.
        </p>
      </section>
      ) : view === "systems" ? (
        <SystemsWorkspace
          key={systemFocusId ?? "systems"}
          connectors={connectors}
          events={observedEvents}
          loading={loading}
          focusId={systemFocusId}
        />
      ) : view === "simulations" ? (
        <SimulationsWorkspace
          key={detailFocus?.view === "simulations" ? `${detailFocus.id}:${detailFocus.researchCycle ?? "latest"}` : "simulations"}
          focusId={detailFocus?.view === "simulations" ? detailFocus.id : null}
          focusCycle={detailFocus?.view === "simulations" ? detailFocus.researchCycle : null}
          connectors={connectors}
          runs={simulationRuns}
          loading={loading}
          onRun={() => setSimulationRevision((revision) => revision + 1)}
          onOpenBelief={(id, researchCycle) => { setDetailFocus({ view: "world-model", id, researchCycle }); void requestView("world-model"); }}
        />
      ) : view === "agent" || view === "media" ? null : view === "world-model" ? (
        <WorldModelWorkspace
          graphFocusNodeId={detailFocus?.view === "world-model" ? detailFocus.id : null}
          graphSnapshotId={detailFocus?.view === "world-model" ? detailFocus.snapshotId ?? null : null}
          graphResearchCycle={detailFocus?.view === "world-model" ? detailFocus.researchCycle ?? null : null}
          onOpenGraphRecord={(targetView, targetId) => {
            const record = targetId ? knowledgeRecords.find(entry => entry.navigation.view === targetView && entry.navigation.targetId === targetId) : undefined;
            if (record) { openKnowledgeRecord(record); return; }
            if (targetView === "sources") {
              const id = targetId?.replace(/^source-/, "");
              if (id && sourceById.has(id as ConnectorId)) setSelectedSourceId(id);
              setSourceCategory("all"); setSourceQuery(""); void requestView("sources");
            } else if (targetView === "systems") { setSystemFocusId(targetId?.replace(/^system-/, "") ?? null); void requestView("systems"); }
            else if (targetView === "simulations" || targetView === "portfolio") {
              if (targetId) setDetailFocus({ view: targetView, id: targetId.replace(/^(simulation|portfolio)-/, "") });
              void requestView(targetView);
            } else if (targetView === "timeline") { setTimelineMode("observed"); setTimelineLens("unified"); void requestView("timeline"); }
          }}
          onDirtyChange={setUnsavedWorkflow}
          engineOptions={{
            sources: sourceAuthority.nodes.map((node) => {
              const connector = node.connectorId ? sourceById.get(node.connectorId) : null;
              return {
                id: node.id,
                label: node.label,
                status: node.status,
                kind: sourceAuthorityNodeKindLabels[node.kind],
                url: node.url,
                relationshipCount: sourceRelationshipCounts.get(node.id) ?? 0,
                claimExtractionSupported: connector?.capabilities.transcripts ?? false,
              };
            }),
            sourceRelationships: sourceAuthority.relationships,
            systems: systems.map((system) => ({ id: system.id, label: system.name })),
            simulations: simulationRuns.map((run) => ({ id: run.modelId, label: run.modelId })),
            positions: portfolioPositions.map((position) => ({
              id: position.id,
              label: `${position.symbol} · ${position.name}`,
            })),
          }}
          onOpenSources={() => void requestView("sources")}
          onOpenSystems={() => void requestView("systems")}
          onOpenTimeline={() => void requestView("timeline")}
          onOpenSimulations={() => void requestView("simulations")}
          onOpenPortfolio={() => void requestView("portfolio")}
        />
      ) : (
        <PortfolioWorkspace
          key={detailFocus?.view === "portfolio" ? detailFocus.id : "portfolio"}
          focusId={detailFocus?.view === "portfolio" ? detailFocus.id : null}
          positions={portfolioPositions}
          webullProductionConnection={webullProductionConnection}
          webullPaperConnection={webullPaperConnection}
          polymarketConnection={polymarketConnection}
          kalshiDemoConnection={kalshiDemoConnection}
          kalshiProductionConnection={kalshiProductionConnection}
          coinbasePaperConnection={coinbasePaperConnection}
          coinbaseProductionConnection={coinbaseProductionConnection}
          loading={portfolioLoading}
          loadError={portfolioError}
          onReload={refreshPortfolio}
          onPositionsChange={setPortfolioPositions}
        />
      )}
    </main>
  );
}
